<template>
  <div>
    <InsightsHeader
      :dateValue="dateValue"
      :minDate="minDate"
      :maxDate="maxDate"
      @add="showSlideOut = true"
      @complete="getAndSetOverviewData"
      @dateApply="handleDateApply"
    ></InsightsHeader>
    <InsightsSlideOut
      :show="showSlideOut"
      @cancel="showSlideOut = false"
      @complete="closeSlideOutAndUpdateOverview"
    />

    <div>
      <div v-if="dashboardLoading" class="insights-loader">
        <Loader
          :size="90"
          :width="9"
        />
      </div>
      <div v-else>
        <div
          class="dashboard-row top-level-metrics"
        >
          <KpiCard
            v-for="(kpiCard, key) in scorecardsMap"
            :key="key"
            :metric="kpiCard.metrics[0]"
            :tooltip="kpiCard.tooltip"
            :data="getInsightsKpiDataProp(key)"
            :loading="dashboardLoading"
            :error="dashboardOrInsightsError"
            :hideRateChange="!isValidMinDate"
            :showExpandIcon="true"
            @iconClick="emitScorecardIconClick(kpiCard.title)"
          />
        </div>
        <div
          class="dashboard-row-tile"
        >
          <DataTile
            v-for="(data, key) in topRows"
            :key="key"
            :title="data.title"
            :class="data.title"
            :data="getInsightsDataProp(key)"
            :tooltip="data.tooltip"
            :metrics="data.metrics"
            :dimensions="data.dimensions"
            :initialSort="data.initialSort"
            :loading="dashboardLoading"
            :error="dashboardOrInsightsError"
            @iconClick="emitTableIconClick"
          />
        </div>
        <div
          class="shopper-intelligence-section"
          v-if="isDemographicsSectionAllowed"
        >
          <DemographicsInsightsContent
            :loading="dashboardLoading"
            :error="dashboardOrDemographicsError"
            @iconClick="emitInsightsIconClick"
          />
        </div>
        <div
          class="dashboard-row-tile"
        >
          <DataTile
            v-for="(data, key) in bottomRows"
            :key="key"
            :title="data.title"
            :class="data.title"
            :data="getInsightsDataProp(key)"
            :tooltip="data.tooltip"
            :metrics="data.metrics"
            :dimensions="data.dimensions"
            :initialSort="data.initialSort"
            :loading="dashboardLoading"
            :error="dashboardOrInsightsError"
            @iconClick="emitTableIconClick"
          />
        </div>
      </div>
    </div>
    <OnboardingTour
      v-if="!dashboardLoading"
      :tourSteps="steps"
      tourName="Insights"
    ></OnboardingTour>
  </div>
</template>
<script>
import { isEqual } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { getInsightsCardMapForSection, getInsightsCardForTitle } from './config/insightsCards';
import { insightsTourSteps } from './config/onboardingTourSteps';
import eventDispatcher from '../../../events/dispatch';
import {
  getDashboardInsightsData,
  getDemographicsOverview,
} from '../../../services/dashboard/eventApiService';
import InsightsSlideOut from '../Common/SlideOut.vue';
import InsightsHeader from '../Insights/InsightsHeader.vue';
import DataTile from '../Common/DataTile.vue';
import OnboardingTour from './OnboardingTour';
import KpiCard from '../Common/KpiCard/KpiCard.vue';
import Loader from '../Common/Loader.vue';
import { CommonLogger } from '../../../services/logger';
import DemographicsInsightsContent from '../Demographics/DemographicsInsightsContent.vue';
import {
  getFilterSelectionsTotalCount,
  getErrorMessage,
} from '../../../services/dashboard/dashboardUtils';
import {
  getSelectedFilters,
} from '../../../services/dashboard/filtersService';
import { dashboardMessages } from './config/dashboardMessages';

export default {
  props: {
    minDate: Date,
    maxDate: Date,
    dateValue: Object,
    startDateStr: String,
    endDateStr: String,
    isValidDateRange: Boolean,
    isValidMinDate: Boolean,
    brandIds: Array,
    brandNames: Array,
    brandIdsStr: String,
  },
  components: {
    InsightsSlideOut,
    InsightsHeader,
    OnboardingTour,
    KpiCard,
    DemographicsInsightsContent,
    DataTile,
    Loader,
  },
  data() {
    return {
      dashboardError: false,
      demographicsError: false,
      insightsError: false,
      dashboardLoading: true,
      steps: insightsTourSteps,
      viewName: 'Insights',
      showSlideOut: false,
      avgTimeSpentDeprecationMessage: dashboardMessages.avgTimeSpentDeprecation,
    };
  },
  computed: {
    ...mapState({
      portfolioBrands: state => state.dashboard.portfolioBrands,
      currentBrand: state => state.attachStudio.currentAttachBrand,
      insightsData: state => state.dashboard.insightsData,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      allowedFilters: state => state.filters.allowedFilters.insights,
      currentUser: state => state.main.currentUser,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
    }),
    ...mapGetters([
      'hasUSBrandSelected',
      'hasNonUSBrandSelected',
      'selectedBrands',
      'selectedBrandIds',
    ]),
    scorecardsMap() {
      return this.getCardsForSection(0);
    },
    topRows() {
      return this.getCardsForSection(1);
    },
    bottomRows() {
      return this.getCardsForSection(2);
    },
    filters() {
      return getSelectedFilters(
        this.dashboardSelectedFilters,
        this.allowedFilters,
      );
    },
    isValidReportRequest() {
      return this.isValidDateRange
                && this.brandIds
                && this.brandIds.length >= 1;
    },
    selectedDemographicsBrands() {
      return this.selectedBrands.filter(pb => pb.isDashboardDemographicsEnabled);
    },
    isDemographicsSectionAllowed() {
      return this.selectedBrands.some(pb => {
        return !pb.optOutLiveRamp && pb.isDashboardDemographicsEnabled;
      });
    },
    hasDemographicsBrandsSelected() {
      return this.selectedDemographicsBrands.length > 0;
    },
    demographicsBrandIds() {
      return this.selectedDemographicsBrands.map(b => b.value);
    },
    stringifiedFilters() {
      return JSON.stringify(this.filters);
    },
    isNewState() {
      return !this.insightsData
                || Object.keys(this.insightsData.uiData)
                  .some(key => this.insightsData.uiData[key] !== this[key]);
    },
    filterDimensions() {
      return this.filters.map(f => f.key);
    },
    dashboardOrInsightsError() {
      return this.dashboardError || this.insightsError;
    },
    dashboardOrDemographicsError() {
      return this.dashboardError || this.demographicsError;
    },
    filterSelectionsTotalCount() {
      if (!this.filters) return 0;

      return getFilterSelectionsTotalCount(this.filters);
    },
    analyticsObj() {
      return {
        dashboard_view: this.viewName,
        start_date: this.startDateStr,
        end_date: this.endDateStr,
        brand_ids: this.brandIdsStr,
        brand_names: this.brandNames.join(', '),
        brand_count: this.brandIds.length,
        filters: this.stringifiedFilters,
        filter_count: this.filters.length,
        filter_selections_total_count: this.filterSelectionsTotalCount,
        filter_stringified_length: this.stringifiedFilters.length,
        filter_dimensions: this.filterDimensions.join(', '),
        filter_dimensions_count: this.filterDimensions.length,
      };
    },
  },
  beforeMount() {
    this.$store.commit('updateActiveView', this.viewName);
  },
  mounted() {
    if (this.isNewState) {
      this.getAndSetOverviewData();
    }
    else {
      this.dashboardLoading = false;
      this.$store.commit('setIsLoading', false);
    }
  },
  methods: {
    closeSlideOutAndUpdateOverview() {
      this.showSlideOut = false;
      this.getAndSetOverviewData();
    },
    handleDateApply(value) {
      const { predefinedRangeValue, range } = value;

      this.$store.commit('updatePredefinedRangeValue', predefinedRangeValue);

      this.$store.commit('updateReportEndDate', range.end);
      this.$store.commit('updateReportStartDate', range.start);

      this.$nextTick(() => {
        this.getAndSetOverviewData();
      });
    },
    async getAndSetOverviewData() {
      if (!this.isValidReportRequest) {
        this.dashboardError = true;
        this.dashboardLoading = false;
        this.$store.commit('setIsLoading', false);

        return;
      }

      this.dashboardError = false;
      this.demographicsError = false;
      this.insightsError = false;
      this.dashboardLoading = true;
      this.$store.commit('setIsLoading', true);
      let errorMessage = '';

      eventDispatcher('dashboardRequest', {
        ...this.analyticsObj,
      });

      try {
        if (this.hasDemographicsBrandsSelected) {
          try {
            const demographicsData = await this.getDemographicsOverviewData();

            this.setDemographicsData(demographicsData);
          } catch (e) {
            this.setDemographicsData(undefined);
            this.demographicsError = true;
            this.$emit('showMessage', { type: 'dashboardError' });
          }
        }

        const data = await this.getOverview();

        if (data) {
          data.uiData = {
            brandIdsStr: this.brandIdsStr,
            startDateStr: this.startDateStr,
            endDateStr: this.endDateStr,
            stringifiedFilters: this.stringifiedFilters,
          };
        }

        this.setInsightsData(data);
      } catch (error) {
        this.insightsError = true;
        errorMessage = getErrorMessage(error);
        this.$emit('showMessage', { type: 'dashboardError' });
      } finally {
        this.dashboardLoading = false;
        this.$store.commit('setIsLoading', false);
        eventDispatcher('dashboardRequestResponse', {
          ...this.analyticsObj,
          has_error: this.insightsError || this.demographicsError,
          has_insights_error: this.insightsError,
          has_demographics_error: this.demographicsError,
          error_message: errorMessage,
        });
      }
    },
    async getOverview() {
      const token = await this.$auth.getTokenSilently();
      const { data } = await getDashboardInsightsData({
        brandIds: this.brandIds,
        startDate: this.startDateStr,
        endDate: this.endDateStr,
        filters: this.filters,
        token,
      });

      this.logNoDataProps(data);

      return data;
    },
    async getDemographicsOverviewData() {
      const token = await this.$auth.getTokenSilently();
      const { data } = await getDemographicsOverview({
        brandIds: this.demographicsBrandIds,
        startDate: this.startDateStr,
        endDate: this.endDateStr,
        filters: this.filters,
        token,
      });

      return data;
    },
    logNoDataProps(data) {
      const noDataProps = this.getNoDataProps(data);

      if (noDataProps.length) {
        const logData = {
          startDate: this.startDateStr,
          endDate: this.endDateStr,
          brandIds: this.brandIds,
          filters: this.stringifiedFilters,
          email: this.currentUser && this.currentUser.email,
          userId: this.currentUser && this.currentUser.id,
        };

        for (const field of noDataProps) {
          const propLogData = {
            ...logData,
            field: field,
          };

          CommonLogger.warn('No data received for insights overview field', propLogData);
        }
      }
    },
    getNoDataProps(data) {
      const props = Object.keys(data);
      const metrics = ['purchaseIntentRate', 'purchaseIntentClicks', 'purchaseIntentValue', 'pageViews'];

      return props.filter(prop => {
        return metrics.includes(prop)
          ? !(data[prop] && data[prop].rows.length)
          : !(data[prop] && data[prop].length);
      });
    },
    setInsightsData(data) {
      this.$store.commit('updateInsightsData', data);
    },
    setDemographicsData(data) {
      this.$store.commit('updateDemographicsOverviewData', data);
    },
    getInsightsKpiDataProp(prop) {
      return this.insightsData && this.insightsData[prop]
        ? this.insightsData[prop]
        : {};
    },
    getInsightsDataProp(prop) {
      return this.insightsData && this.insightsData[prop]
        ? this.insightsData[prop].slice(0, 5)
        : [];
    },
    getDataForTitle(title) {
      const { initialSort, dimensions, metrics, filters } = getInsightsCardForTitle(title);

      return {
        initialSort,
        dimensions,
        metrics,
        filters,
      };
    },
    emitScorecardIconClick(title) {
      const explorerData = this.getDataForTitle(title);

      this.emitExplorerIconClick(title, explorerData);
    },
    emitTableIconClick(iconData) {
      const { title } = iconData;

      const explorerData = this.getDataForTitle(title);

      this.emitExplorerIconClick(title, explorerData);
    },
    emitInsightsIconClick(title) {
      const insightData = this.getDataForTitle(title);

      this.emitShopperIntelIconClick(title, insightData);
    },
    emitExplorerIconClick(title, explorerData) {
      this.$emit('insightsExpandIconClick', {
        title,
        view: 'Explorer',
        data: explorerData,
      });
    },
    emitShopperIntelIconClick(title, shopperIntelData) {
      this.$emit('insightsExpandIconClick', {
        title,
        view: 'Shopper Intelligence',
        data: shopperIntelData,
      });
    },
    getCardsForSection(sectionNum) {
      const allCardsForSection = getInsightsCardMapForSection(sectionNum);

      return this.filterAllowedCards(allCardsForSection);
    },
    filterAllowedCards(cardMap) {
      return Object.keys(cardMap).reduce((filteredCardMap, key) => {
        const cardConfig = cardMap[key];

        if (this.isAllowedCard(cardConfig)) {
          filteredCardMap[key] = cardConfig;
        }

        return filteredCardMap;
      }, {});
    },
    isAllowedCard(card = {}) {
      const { isForUSBrandsOnly, isForNonUSBrandsOnly } = card;

      if (isForUSBrandsOnly && !this.hasUSBrandSelected) return false;
      if (isForNonUSBrandsOnly && !this.hasNonUSBrandSelected) return false;

      return true;
    },
  },
  watch: {
    async selectedBrandIds(newIds, oldIds) {
      if (!this.showNewBrandSelector) return;

      if (this.isNewState && !isEqual(newIds, oldIds)) {
        this.$emit('clearSelectedFilters');
        await this.getAndSetOverviewData();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.dashboard-row-tile {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ::v-deep .tile {
    margin: 0px;
    height: auto;
    flex-grow: 1;
  }
}

.dashboard-row {
  display: flex;
  flex-direction: row;
  height: auto;
  flex-wrap: wrap;
}

.dashboard-row {
  div:first-of-type {
    margin-left: 0px;
  }
  div:last-of-type {
    margin-right: 0px;
  }
}

.last {
  div {
    flex: 1;
  }
}
</style>
<style>

.insights-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 120px;
}

</style>
